import 'regenerator-runtime/runtime';
import { call, select, take, takeLatest } from 'redux-saga/effects';
import {
  AngebotsId,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  QuoteDbCheckoutPayload,
  QuoteDbDashboardPayload,
  QuoteDbInitPayload,
  QuoteDbRequestPayload,
  QuoteDbSelectedAppsItem,
  QuoteDbSpartePayload,
  QuoteDbToolbarPayload,
  QuoteDbVorversicherung,
  storeIsAvailable,
} from '@ps20-frontend/shop-integration-redux';
import { QuoteDbRequest, QuoteVorversicherung } from '@ps20/bff-models';
import { App, getSpartenTypeByApp, isMultistoreApp } from '@ps20-frontend/frontend-models';
import { TaasData } from '@ps20-frontend/preis-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { IAppState } from '../localStore';
import { mapMetadaten, mapSpartenDaten, mapWarenkorb } from './helpers/quoteDBMapper';

export enum QUOTEDB_REQUEST_EVENTS {
  QUOTEDB_REQUEST_SUCCESSFUL = 'QUOTEDB_REQUEST_SUCCESSFUL',
  QUOTEDB_REQUEST_FAILED = 'QUOTEDB_REQUEST_FAILED',
}

export function* watchDoQuoteDBRequest() {
  yield takeLatest(GlobalActions.QUOTEDB_INIT, safeSaga(doQuoteDBRequestSaga, App.SHOP));
}

export function* doQuoteDBRequestSaga(action: GlobalAction<QuoteDbInitPayload>) {
  const state: IAppState = yield select();
  const quoteDbdaten: QuoteDbRequest = yield call(getQuoteDbDaten, action, state);
  const urlQuoteDB = '/privatschutz/api/quoteDb';

  const globalPut = GlobalPut.getInstance();
  const requestAction = new GlobalActions().doRequest({
    appId: App.SHOP,
    body: quoteDbdaten,
    url: urlQuoteDB,
    successEvent: QUOTEDB_REQUEST_EVENTS.QUOTEDB_REQUEST_SUCCESSFUL,
    errorEvent: QUOTEDB_REQUEST_EVENTS.QUOTEDB_REQUEST_FAILED,
    noCache: true,
  });
  yield call(globalPut.put, requestAction);
}

export function* getQuoteDbDaten(action: GlobalAction<QuoteDbInitPayload>, state: IAppState) {
  const globalPut = GlobalPut.getInstance();
  yield call(globalPut.put, new GlobalActions().quoteDBDashboardDataRequested());
  const quoteDbDataDashboard: GlobalAction<QuoteDbDashboardPayload> = yield take(
    GlobalActions.QUOTEDB_DATA_DASHBOARD_GENERATED,
  );
  let angebotsIds: AngebotsId[] = [];
  if (quoteDbDataDashboard?.payload) {
    angebotsIds = quoteDbDataDashboard.payload.selectedApps.map(app => ({
      angebotsId: app.angebotsId,
      appId: app.appId,
    }));
  }

  yield call(globalPut.put, new GlobalActions().quoteDBToolbarDataRequested());
  const quoteDbDataToolbar: GlobalAction<QuoteDbToolbarPayload> = yield take(
    GlobalActions.QUOTEDB_DATA_TOOLBAR_GENERATED,
  );

  yield call(globalPut.put, new GlobalActions().quoteDBCheckoutDataRequested());
  const quoteDbDataCheckout: GlobalAction<QuoteDbCheckoutPayload> = yield take(
    GlobalActions.QUOTEDB_DATA_CHECKOUT_GENERATED,
  );

  const quoteDbRequestPayload: QuoteDbRequestPayload = {
    angebotsIds,
    dialogschritt: action.payload.dialogschritt,
  };
  yield call(globalPut.put, new GlobalActions().quoteDBDataRequested(quoteDbRequestPayload));
  const quoteDbDataSparte: Array<QuoteDbSpartePayload<TaasData>> = [];
  if (quoteDbDataDashboard?.payload) {
    for (const _produktImWarenkorb of quoteDbDataDashboard.payload.selectedApps) {
      if (
        !isMultistoreApp(_produktImWarenkorb.appId) ||
        storeIsAvailable(getAngebotsId(_produktImWarenkorb))
      ) {
        const quoteDbSparten: GlobalAction<QuoteDbSpartePayload<TaasData>> = yield take(
          GlobalActions.QUOTEDB_DATA_SPARTE_GENERATED,
        );
        quoteDbDataSparte.push(quoteDbSparten.payload);
      }
    }
  }
  const quoteDbInitPayload: QuoteDbInitPayload = action.payload;
  const isGeschaeftsFuehrerRs: boolean = quoteDbDataSparte.find(
    quoteDbSparte => quoteDbSparte.angebotsId?.appId === App.RS,
  )?.geschaeftsfuehrer;
  const quoteDBRequestModel: QuoteDbRequest = {
    dialogschritt: action.payload.dialogschritt,
    warenkorb: mapWarenkorb(quoteDbDataDashboard.payload, quoteDbDataSparte),
    quoteMetadaten: mapMetadaten(
      quoteDbDataToolbar.payload,
      quoteDbInitPayload,
      quoteDbDataCheckout.payload,
      state,
      isGeschaeftsFuehrerRs,
    ),
    quoteVorversicherungen: convertToQuoteVorversicherungen(
      quoteDbDataCheckout.payload.vorversicherungen,
    ),
    spartenDaten: mapSpartenDaten(
      quoteDbDataSparte,
      quoteDbDataToolbar.payload,
      quoteDbDataDashboard.payload,
      quoteDbDataCheckout.payload,
      action.payload,
      state,
    ).filter(sparteDaten => sparteDaten.tarifierungsVektor.length > 0),
  };

  if (quoteDbInitPayload.ausloeserAngebotsId) {
    quoteDBRequestModel.ausloeserAngebotsId = quoteDbInitPayload.ausloeserAngebotsId.angebotsId;
    quoteDBRequestModel.ausloeserLob = getSpartenTypeByApp(
      quoteDbInitPayload.ausloeserAngebotsId.appId,
    );
  }

  return quoteDBRequestModel;
}

export function convertToQuoteVorversicherungen(
  payloadVorversicherungen: Array<QuoteDbVorversicherung>,
): Array<QuoteVorversicherung> {
  if (!payloadVorversicherungen) {
    return [];
  }
  return payloadVorversicherungen.map(
    payloadVorversicherung =>
      ({
        gesellschaft: payloadVorversicherung.gesellschaft,
        spartenType: getSpartenTypeByApp(payloadVorversicherung.appId),
        angebotsId: payloadVorversicherung.angebotsId,
      }) as QuoteVorversicherung,
  );
}

function getAngebotsId(selectedApp: QuoteDbSelectedAppsItem): AngebotsId {
  return {
    appId: selectedApp.appId,
    angebotsId: selectedApp.angebotsId,
  };
}
